<template>
  <b-container>
    <header class="header">
      <TheTitle class="header__title">收款狀態</TheTitle>
      <div class="pt-4 pb-2">
       <SearchBar
          class="header__search"
          v-if="role === 'admin'"
          placeholder="搜尋商戶名稱"
          v-model.trim="merchantName"
        ></SearchBar>
      </div>
    </header>
    <Withdraw
      :role="role"
      :loading="loading"
      :overview="overview"
    ></Withdraw>
    <TheListingTitle
      title="紀錄列表"
      :selectedKeywordAndFilters="selectedKeywordAndFilters"
      @reset-click="initialize"
    >
    </TheListingTitle>
    <section class="table-content">
      <template>
        <BaseFilterToolbar @reset="initialize" :loading="loading">
        <div class="d-flex">
          <label>
            <b-form-select class="filter keyword-select" v-model="filter.keywordType">
              <b-form-select-option
                  v-for="option in filteredKeywordSelectOptions"
                  :value="option.value"
                  :key="option.text"
                >
                  {{ option.text }}
                </b-form-select-option>
            </b-form-select>
          </label>
          <label id="keyword-filter">
            <input
              v-model.trim="keyword"
              type="text"
              class="form-control"
              placeholder="關鍵字..."
              aria-label="關鍵字..."
              aria-describedby="button-addon1"
            />
          </label>
        </div>
            <label>
              <b-form-select class="filter" v-model="filter.status">
                <b-form-select-option
                v-for="option in filteredStatusSelectOptions"
                :value="option.value"
                :key="option.text"
              >
                {{ option.text }}
              </b-form-select-option>
              </b-form-select>
            </label>
            <DateTimePicker
              v-model="dateRange"
              has-range
              placeholder="選擇日期"
            ></DateTimePicker>
            <template v-slot:end>
              <BaseButton v-stream:click="exportData$" class="app--mobile-hidden">匯出</BaseButton>
            </template>
        </BaseFilterToolbar>
        <DataTable
          v-show="list"
          :fields="fields"
          :mobileFields="mobileFields"
          :items="list"
          :total-result="total"
          :options.sync="options"
          :loading="loading"
          selectable
          @row-clicked="goToItem"
          @row-selected="onRowSelected"
          mobile-searchable
          mobile-exportable
          v-stream:export-click="exportData$"
          showRowDetails
        >
          <template v-slot:cell(profit)="{ item }">
            <div class="d-flex flex-column">
              {{ item.profit }}
              <br />
              <div class="text-secondary">{{ item.billDate }}</div>
            </div>
          </template>
          <template v-slot:cell(statement)="{ value }">
            <DownloadButton :pdfSrc="value"></DownloadButton>
          </template>
          <template v-slot:cell(status)="{ value }">
            <WithdrawalStatus :status="value"></WithdrawalStatus>
          </template>

          <!-- mobile only -->
          <template v-slot:statement="{ item }">
            <DownloadButton :pdfSrc="item.statement"></DownloadButton>
          </template>
          <template v-slot:status="{ item }">
            <WithdrawalStatus :status="item.status"></WithdrawalStatus>
          </template>
          <!-- end of mobile only -->
        </DataTable>
        <b-skeleton-table v-if="!list" :rows="15" :columns="5"></b-skeleton-table>
      </template>
    </section>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import {
  tap, map, switchMap, debounceTime, distinctUntilChanged, pluck, mergeAll,
} from 'rxjs/operators';
import axios from 'axios';
import {
  BFormSelect, BFormSelectOption, BContainer, BSkeletonTable,
} from 'bootstrap-vue';
import DateTimePicker from '@/components/DateTimePicker.vue';
import BaseButton from '@/components/BaseButton.vue';
import dataTableMixin from '../../mixins/data-table';
import exportMixin from '../../mixins/export';
import filtersQueryStringMixin from '../../mixins/filtersQueryString';
import optionsQueryStringMixin from '../../mixins/optionsQueryString';
import TheTitle from '../../components/TheTitle.vue';
import TheListingTitle from '../../components/TheListingTitle.vue';
import DataTable from '../../components/DataTable.vue';
import DownloadButton from '../../components/DownloadButton.vue';
import Withdraw from '../../components/record/Withdraw.vue';
import SearchBar from '../../components/SearchBar.vue';
import WithdrawalStatus from '../../components/WithdrawalStatus.vue';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';

const MERCHANT_HEADERS = [
  { key: 'id', label: '紀錄編號', sortable: true },
  { key: 'billDate', label: '帳單日期', sortable: true },
  { key: 'transferDate', label: '匯款日期', sortable: true },
  { key: 'amount', label: '銷售額', sortable: true },
  { key: 'profit', label: '可收取款項', sortable: true },
  { key: 'others', label: '其他費用', sortable: true },
  { key: 'statement', label: '月結單', sortable: true },
  { key: 'status', label: '已匯款', sortable: true },
];

const ADMIN_HEADERS = [
  { key: 'id', label: '紀錄編號', sortable: true },
  { key: 'merchantName', label: '商戶名稱', sortable: true },
  { key: 'merchantId', label: '商戶編號', sortable: true },
  { key: 'brandName', label: '品牌名稱', sortable: true },
  { key: 'billDate', label: '帳單日期', sortable: true },
  { key: 'transferDate', label: '匯款日期', sortable: true },
  { key: 'amount', label: '銷售額', sortable: true },
  { key: 'profit', label: '可收取款項', sortable: true },
  { key: 'createdAt', label: '紀錄創建時間', sortable: true },
  { key: 'others', label: '其他費用', sortable: true },
  { key: 'statement', label: '月結單', sortable: true },
  { key: 'status', label: '已匯款', sortable: true },
];

const MOBILE_HEADER = [
  {
    key: 'id',
    label: '編號',
    thStyle: {
      width: '3rem',
    },
  },
  {
    key: 'profit',
    label: '可收取款項',
  },
  {
    key: 'status',
    thStyle: {
      display: 'none',
    },
    isShownInDetails: true,
  },
];

export default {
  mixins: [dataTableMixin, exportMixin, filtersQueryStringMixin, optionsQueryStringMixin],
  components: {
    BFormSelect,
    BFormSelectOption,
    BContainer,
    BSkeletonTable,
    TheTitle,
    TheListingTitle,
    DataTable,
    BaseButton,
    DownloadButton,
    Withdraw,
    SearchBar,
    WithdrawalStatus,
    DateTimePicker,
    BaseFilterToolbar,
  },
  data: () => ({
    merchantName: null,
    filter: {
      keywordType: 'all',
      status: undefined,
    },
    options: {
      // sortBy: 'id',
    },
    keyword: '',
    keywordSelectOptions: [
      { value: 'all', text: '所有關鍵字' },
      { value: 'id', text: '參考編號' },
      { value: 'merchantId', text: '商戶編號', adminOnly: true },
      { value: 'merchantName', text: '商戶名稱', adminOnly: true },
      { value: 'brandName', text: '品牌名稱', adminOnly: true },
    ],
    statusSelectOptions: [
      { value: undefined, text: '匯款狀態' },
      { value: 'PENDING', text: '待審核' },
      { value: 'FINISHED', text: '已匯款' },
    ],
    selected: [],
    dateRange: null,
    mobileFields: MOBILE_HEADER,
  }),
  computed: {
    ...mapGetters(['role']),
    fields() {
      return this.role === 'merchant' ? MERCHANT_HEADERS : ADMIN_HEADERS;
    },
  },
  domStreams: ['onOverviewSelect$', 'exportData$'],
  subscriptions() {
    const getData$ = (data) => this.httpRequest$({
      method: 'GET',
      url: `/agc-${this.role}-api/paymentrecord`,
    }, data);

    const getOverview$ = (data) => this.httpRequest$({
      method: 'GET',
      url: `/agc-${this.role}-api/overview`,
    }, data);

    const onMerchantNameUpdate$ = this.$watchAsObservable('merchantName').pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        if (this.keyword) {
          this.keyword = null;
          this.filter.merchantName = this.merchantName;
        } else {
          this.filter.merchantName = this.merchantName;
        }
      }),
    );

    const onDateRangeUpdate$ = this.$watchAsObservable('dateRange').pipe(
      pluck('newValue'),
      tap((dateRange) => {
        if (dateRange) {
          [this.filter.fromDate, this.filter.toDate] = dateRange;
        } else {
          this.filter.fromDate = null;
          this.filter.toDate = null;
        }
      }),
    );

    return {
      list: scheduled([
        ...this.scheduleTasks,
        onMerchantNameUpdate$,
        onDateRangeUpdate$,
      ], asyncScheduler).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
          if (this.merchantName && this.keyword) {
            this.filter.merchantName = null;
          }
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(getData$),
      ),
      overview: scheduled([
        ...this.scheduleTasks,
        onMerchantNameUpdate$,
        onDateRangeUpdate$,
      ], asyncScheduler).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(getOverview$),
      ),
      exportData: this.exportData$.pipe(
        switchMap(() => axios({
          method: 'GET',
          url: `/agc-${this.role}-api/export/records`,
          params: {
            ...this.filter,
            q: this.keyword,
            order: 'desc',
            selectedItem: this.selected.map((item) => item.id).join(','),
          },
          responseType: 'blob',
        }).then((res) => {
          this.downloadFile(res.data, this.generateFileName('payment-record-list-data'));
        })),
      ),
    };
  },
  methods: {
    goToItem({ id }) {
      this.$router.push({
        path: `${this.$route.path}/${id}`,
      });
    },
    onRowSelected(items) {
      this.selected = items;
    },
    onOverviewItemClicked(status) {
      this.filter.status = status.toUpperCase();
    },
    initialize() {
      this.filter.keywordType = 'all';
      this.keyword = '';
      this.filter.status = undefined;
      this.filter.tagId = undefined;
      this.merchantName = undefined;
      this.dateRange = '';
      setTimeout(() => {
        this.loading = false;
      }, 550);
      // when user updates filter too fast, the loading state will not set back to false
      // this time must be bigger than debounceTime's time
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/data-table/list-table-content';
@import '../../assets/scss/listing/header';

.text-secondary {
  @include respond(phone) {
    font-size: 12px;
  }
}
</style>
